import { useState } from 'react'
import { Avatar, Button, Chip, PriorityChip } from '@/components/ui'
import { Clock, MapPin, DollarSign, Calendar, Flame, Filter, History } from 'lucide-react'
import { fmtMoney } from '@/lib/utils'
import { cn } from '@/lib/utils'
import type { Prioridad } from '@/mock'

interface ClienteCola {
  pos: number
  cliente: string
  cedula: string
  wa: string
  prioridad: Prioridad
  razon: string
  razonIcon: string
  mensaje: string
  minutos: number
  mora: number
  monto: number
  ciudad: string
}

const colaMock: ClienteCola[] = [
  { pos: 1, cliente: 'Pablo Mendoza', cedula: '0908112233', wa: '+593 99 XXX 5555', prioridad: 'P1', razon: 'Tono agresivo', razonIcon: '⚠', mensaje: 'Si no me solucionan esto hoy voy a hacer una denuncia pública...', minutos: 22, mora: 45, monto: 340.75, ciudad: 'Guayaquil' },
  { pos: 2, cliente: 'Lucía Benítez', cedula: '0913445566', wa: '+593 99 XXX 1122', prioridad: 'P2', razon: 'Plan de pago custom', razonIcon: '💼', mensaje: 'Necesito pagar en 6 cuotas de $85 pero ninguna opción me permite...', minutos: 18, mora: 28, monto: 512.00, ciudad: 'Quito' },
  { pos: 3, cliente: 'Jorge Intriago', cedula: '0908112233', wa: '+593 99 XXX 3344', prioridad: 'P2', razon: 'IA no pudo (3 intentos)', razonIcon: '🤖', mensaje: 'Es que ya les expliqué 3 veces, mi caso no es como los otros, tengo...', minutos: 15, mora: 8, monto: 145.00, ciudad: 'Manta' },
  { pos: 4, cliente: 'Carlos Macías', cedula: '0920115588', wa: '+593 99 XXX 7788', prioridad: 'P3', razon: 'Pidió asesor', razonIcon: '💬', mensaje: 'Hola, me gustaría hablar con un asesor por favor', minutos: 8, mora: 3, monto: 89.00, ciudad: 'Guayaquil' },
  { pos: 5, cliente: 'Ana Paola Vera', cedula: '0914778899', wa: '+593 99 XXX 2211', prioridad: 'P3', razon: 'Comprobante rechazado 2x', razonIcon: '📸', mensaje: 'Ya mandé el comprobante dos veces y me dicen que está mal', minutos: 5, mora: 20, monto: 178.50, ciudad: 'Guayaquil' },
]

const filtros = [
  { k: 'todos', l: 'Todos', n: 14 },
  { k: 'p1', l: '🔥 P1 Urgentes', n: 3 },
  { k: 'especialidad', l: 'Mi especialidad', n: 5 },
  { k: 'asesor', l: 'Pidió asesor', n: 5 },
  { k: 'plan', l: 'Plan de pago', n: 4 },
  { k: 'ia-no', l: 'IA no pudo', n: 3 },
  { k: 'agresivo', l: 'Agresivo', n: 2 },
] as const

export function Cola() {
  const [filtro, setFiltro] = useState<string>('todos')
  const [orden, setOrden] = useState<'prio' | 'antiguo' | 'nuevo'>('prio')
  const [tomadas, setTomadas] = useState<Set<number>>(new Set())

  const items = [...colaMock]
    .sort((a, b) => {
      if (orden === 'prio') return a.prioridad.localeCompare(b.prioridad) || b.minutos - a.minutos
      if (orden === 'antiguo') return b.minutos - a.minutos
      return a.minutos - b.minutos
    })
    .filter(c => {
      if (filtro === 'todos') return true
      if (filtro === 'p1') return c.prioridad === 'P1'
      if (filtro === 'asesor') return c.razon.includes('asesor')
      if (filtro === 'plan') return c.razon.toLowerCase().includes('plan')
      if (filtro === 'ia-no') return c.razon.toLowerCase().includes('ia no pudo')
      if (filtro === 'agresivo') return c.razon.toLowerCase().includes('agresivo')
      return true
    })

  return (
    <div className="space-y-5">
      <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-3">
        <Filter className="w-4 h-4 text-slate-400" />
        <div className="flex items-center gap-1 flex-wrap">
          {filtros.map(f => (
            <button
              key={f.k}
              onClick={() => setFiltro(f.k)}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-medium transition',
                filtro === f.k
                  ? 'bg-navy-700 text-white'
                  : 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'
              )}
            >
              {f.l} <span className="opacity-60">· {f.n}</span>
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs">
          <span className="text-slate-500">Ordenar:</span>
          <select
            value={orden}
            onChange={e => setOrden(e.target.value as typeof orden)}
            className="px-2 py-1 border border-slate-200 rounded-md text-xs bg-white"
          >
            <option value="prio">Prioridad + tiempo</option>
            <option value="antiguo">Más antiguo primero</option>
            <option value="nuevo">Más reciente primero</option>
          </select>
        </div>
      </div>

      <div className="space-y-3">
        {items.map(c => {
          const esP1 = c.prioridad === 'P1'
          const tomado = tomadas.has(c.pos)
          return (
            <article
              key={c.pos}
              className={cn(
                'bg-white border rounded-2xl p-4 flex items-start gap-4 transition',
                tomado && 'opacity-50',
                esP1 ? 'border-rose-200 shadow-md shadow-rose-100' : 'border-slate-200'
              )}
            >
              <div className={cn(
                'w-10 h-10 rounded-full flex items-center justify-center font-mono font-bold text-sm shrink-0',
                esP1 ? 'bg-rose-500 text-white' : c.prioridad === 'P2' ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-600'
              )}>
                #{c.pos}
              </div>
              <Avatar name={c.cliente} size="md" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center flex-wrap gap-2">
                  <span className="font-semibold text-navy-700">{c.cliente}</span>
                  <PriorityChip prioridad={c.prioridad} withLabel />
                  <Chip variant="outline">{c.razonIcon} {c.razon}</Chip>
                  <Chip variant="default" className="font-mono">{c.wa}</Chip>
                </div>
                <p className="text-sm text-slate-600 mt-1 line-clamp-1">{c.mensaje}</p>
                <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> esperando <span className={cn('font-mono font-semibold', c.minutos > 15 ? 'text-rose-600' : 'text-navy-700')}>{c.minutos} min</span></span>
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {c.mora} días mora</span>
                  <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> <span className="font-mono font-semibold text-rose-600">{fmtMoney(c.monto)}</span> vencido</span>
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.ciudad}</span>
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Button
                  variant={esP1 ? 'rose' : 'amber'}
                  size="sm"
                  onClick={() => setTomadas(prev => new Set([...prev, c.pos]))}
                  disabled={tomado}
                >
                  <Flame className="w-3.5 h-3.5" /> {esP1 ? 'Tomar YA' : 'Tomar'}
                </Button>
                <Button variant="outline" size="sm">
                  <History className="w-3.5 h-3.5" /> Ver historial IA
                </Button>
              </div>
            </article>
          )
        })}
      </div>

      <button className="w-full py-2.5 border-2 border-dashed border-slate-300 rounded-lg text-sm text-slate-500 hover:border-amber-400 hover:text-amber-600 transition">
        Mostrar los 9 restantes ↓
      </button>
    </div>
  )
}
